import Icon from "assets/icons";
import React from "react";
import { useCopies } from "shared/hooks/useCopies";
import { PreviewProps } from "../types";
import TemplateContainer from "./TemplateContainer";
import { Helmet } from "react-helmet";
import { FontFamilyToUrlMapping } from "../enum";
import { ApiSocialPostTemplate } from "../../list/types";
import { UserDetailsText } from "./UserDetailsText";

export function AvatarDummy() {
  return (
    <div className="defaultAvatarImage">
      <Icon.DefaultAvatar />
    </div>
  );
}

export default function PreviewTemplate({
  url = "",
  hasUserImage,
  hasUserInfo,
  userImagePosition,
  userDetailsPosition,
  hasName,
  hasJobTitle,
  hasCompany,
  children,
  hasContainerBorder,
  ...props
}: PreviewProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const copies = useCopies();

  const { name, jobTitle, company } = React.useMemo(() => {
    return {
      name: props.name ? props.name : copies.get("template.placeholder.name"),
      jobTitle: props.jobTitle ? props.jobTitle : copies.get("template.placeholder.job.title"),
      company: props.company ? props.company : copies.get("template.placeholder.company")
    };
  }, [copies, props.name, props.jobTitle, props.company]);

  return (
    <React.Fragment>
      <PostOnSocialFontLoader styleType={props.styleType} />
      <TemplateContainer continerRef={containerRef} hasContainerBorder={hasContainerBorder} styleType={props.styleType}>
        <img src={url} alt="" className="template--image" />
        {hasUserImage && (
          <div
            style={{
              position: "absolute",
              top: userImagePosition.y,
              left: userImagePosition.x,
              height: userImagePosition.h,
              width: userImagePosition.w
            }}>
            <AvatarDummy />
          </div>
        )}
        {hasUserInfo && (
          <PostOnSocialUserDetails
            userDetailsPosition={userDetailsPosition}
            hasName={hasName}
            hasJobTitle={hasJobTitle}
            hasCompany={hasCompany}
            name={name}
            jobTitle={jobTitle}
            company={company}
            textAlign={props.textAlign}
            primaryColor={props.primaryColor}
            secondaryColor={props.secondaryColor}
            styleType={props.styleType}
            primaryFontSize={props.primaryFontSize}
            secondaryFontSize={props.secondaryFontSize}
          />
        )}
      </TemplateContainer>
      {children}
    </React.Fragment>
  );
}

export function PostOnSocialFontLoader({ styleType }: { styleType: string }) {
  const fontUrl = React.useMemo(() => {
    // @ts-ignore
    return FontFamilyToUrlMapping[styleType];
  }, [styleType]);

  return <Helmet>{fontUrl && <link href={fontUrl} rel="stylesheet" />}</Helmet>;
}

export function PostOnSocialUserDetails({
  userDetailsPosition,
  hasName,
  hasJobTitle,
  hasCompany,
  name,
  jobTitle,
  company,
  ...props
}: {
  userDetailsPosition: { x: number; y: number; h: number; w: number };
  hasName: boolean;
  hasJobTitle: boolean;
  hasCompany: boolean;
  name: string;
  jobTitle: string;
  company: string;
  textAlign?: ApiSocialPostTemplate["textAlign"];
  primaryColor?: string;
  secondaryColor?: string;
  styleType?: string;
  primaryFontSize?: string;
  secondaryFontSize?: string;
}) {
  return (
    <div
      style={{
        position: "absolute",
        top: userDetailsPosition.y,
        left: userDetailsPosition.x,
        height: userDetailsPosition.h,
        width: userDetailsPosition.w,
        fontFamily: "inherit"
      }}>
      <UserDetailsText
        textAlign={props.textAlign}
        hasName={hasName}
        hasJobTitle={hasJobTitle}
        hasCompany={hasCompany}
        name={name}
        jobTitle={jobTitle}
        company={company}
        styleType={props.styleType}
        primaryColor={props.primaryColor}
        secondaryColor={props.secondaryColor}
        primaryFontSize={props.primaryFontSize}
        secondaryFontSize={props.secondaryFontSize}
      />
    </div>
  );
}
